import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { ApiSequenceConfig } from '@chatsdet/types';
import { API_URL } from '../../../../config';
import { toApiError } from '../../../../lib/api-error';

export type SnapshotSummary = {
  version: number;
  name: string | null;
  publishedAt: string;
  publishedBy: string;
};

export type DraftSnapshotSummary = {
  version: string;
  name: string | null;
  createdAt: string;
  basePublishedVersion: number;
};

export type WorkflowRevisionProvenance = {
  source?: string;
  summary?: string;
  actor?: { id?: string; kind?: 'user' | 'agent' | 'system' };
};

export type WorkflowRevisionApiSequence = {
  id: string;
  credentialId: string | null;
  config: ApiSequenceConfig;
  guidancePrompt: string | null;
};

export type WorkflowRevisionSummary = {
  id: string;
  number: number;
  version: string;
  name: string | null;
  contentHash: string | null;
  provenance: WorkflowRevisionProvenance | null;
  createdAt: string;
};

export type WorkflowRevisionDetail = WorkflowRevisionSummary & {
  basePublishedVersion: number;
  snapshotJson: {
    nodes: unknown[];
    edges: unknown[];
    apiSequence: WorkflowRevisionApiSequence | null;
    [key: string]: unknown;
  };
  setupFlowPreviews?: Record<string, unknown>;
};

type WorkflowRevisionWire = Omit<WorkflowRevisionSummary, 'number'> & {
  revisionNumber: number;
  basePublishedVersion?: number;
  snapshotJson?: WorkflowRevisionDetail['snapshotJson'];
  setupFlowPreviews?: Record<string, unknown>;
};

function normalizeWorkflowRevision(revision: WorkflowRevisionWire): WorkflowRevisionDetail {
  return {
    ...revision,
    number: revision.revisionNumber,
    basePublishedVersion: revision.basePublishedVersion ?? 0,
    snapshotJson: revision.snapshotJson ?? { nodes: [], edges: [], apiSequence: null },
  };
}

export type SnapshotDetail = SnapshotSummary & {
  snapshotJson: {
    nodes: unknown[];
    edges: unknown[];
    [key: string]: unknown;
  };
  setupFlowPreviews?: Record<string, unknown>;
};

/**
 * Fetches the list of all published and draft snapshots for a workflow.
 */
export function useWorkflowSnapshots(workflowId: string | null) {
  return useQuery({
    queryKey: ['workflow-snapshots', workflowId],
    queryFn: async (): Promise<{
      snapshots: SnapshotSummary[];
      draftSnapshots: DraftSnapshotSummary[];
    }> => {
      if (!workflowId) return { snapshots: [], draftSnapshots: [] };
      const r = await fetch(`${API_URL}/workflows/${workflowId}/snapshots`, {
        credentials: 'include',
      });
      // 404 is expected when the workflow doesn't exist for the viewer
      // (e.g. switching orgs in another tab) — return empty instead of throwing
      // to avoid noisy Sentry errors.
      if (r.status === 404) return { snapshots: [], draftSnapshots: [] };
      if (!r.ok) throw await toApiError(r, 'Failed to fetch workflow snapshots');
      const data = await r.json();
      return {
        snapshots: data.snapshots ?? [],
        draftSnapshots: data.draftSnapshots ?? [],
      };
    },
    enabled: !!workflowId,
  });
}

/** Fetches the immutable, numbered definition revisions for a workflow. */
export function useWorkflowRevisions(workflowId: string | null) {
  return useQuery({
    queryKey: ['workflow-revisions', workflowId],
    queryFn: async (): Promise<WorkflowRevisionSummary[]> => {
      if (!workflowId) return [];
      const r = await fetch(`${API_URL}/workflows/${workflowId}/revisions`, {
        credentials: 'include',
      });
      if (r.status === 404) return [];
      if (!r.ok) throw await toApiError(r, 'Failed to fetch workflow revisions');
      const data = (await r.json()) as { revisions?: WorkflowRevisionWire[] };
      return (data.revisions ?? []).map(normalizeWorkflowRevision);
    },
    enabled: !!workflowId,
  });
}

/** Fetches one immutable revision definition for the history preview canvas. */
export function useWorkflowRevision(workflowId: string | null, revisionNumber: number | null) {
  return useQuery({
    queryKey: ['workflow-revision', workflowId, revisionNumber],
    queryFn: async (): Promise<WorkflowRevisionDetail | null> => {
      if (!workflowId || revisionNumber === null) return null;
      const r = await fetch(`${API_URL}/workflows/${workflowId}/revisions/${revisionNumber}`, {
        credentials: 'include',
      });
      if (!r.ok) throw await toApiError(r, 'Failed to fetch workflow revision');
      const data = (await r.json()) as { revision?: WorkflowRevisionWire };
      return data.revision ? normalizeWorkflowRevision(data.revision) : null;
    },
    enabled: !!workflowId && revisionNumber !== null,
  });
}

/**
 * Fetches a specific published snapshot version including the full snapshotJson.
 */
export function useWorkflowSnapshot(workflowId: string | null, version: number | null) {
  return useQuery({
    queryKey: ['workflow-snapshot', workflowId, version],
    queryFn: async (): Promise<SnapshotDetail | null> => {
      if (!workflowId || version === null) return null;
      const r = await fetch(`${API_URL}/workflows/${workflowId}/snapshots/${version}`, {
        credentials: 'include',
      });
      if (!r.ok) throw await toApiError(r, 'Failed to fetch workflow snapshot');
      const data = await r.json();
      return data.snapshot ?? null;
    },
    enabled: !!workflowId && version !== null,
  });
}

export type DraftSnapshotDetail = {
  version: string;
  name: string | null;
  createdAt: string;
  basePublishedVersion: number;
  snapshotJson: {
    nodes: unknown[];
    edges: unknown[];
    [key: string]: unknown;
  };
  setupFlowPreviews?: Record<string, unknown>;
};

/**
 * Fetches a specific draft snapshot version including the full snapshotJson.
 */
export function useDraftSnapshot(workflowId: string | null, version: string | null) {
  return useQuery({
    queryKey: ['workflow-draft-snapshot', workflowId, version],
    queryFn: async (): Promise<DraftSnapshotDetail | null> => {
      if (!workflowId || version === null) return null;
      const r = await fetch(`${API_URL}/workflows/${workflowId}/draft-snapshots/${version}`, {
        credentials: 'include',
      });
      if (!r.ok) throw await toApiError(r, 'Failed to fetch draft snapshot');
      const data = await r.json();
      return data.snapshot ?? null;
    },
    enabled: !!workflowId && version !== null,
  });
}

/**
 * Save a named draft version checkpoint.
 */
export function useSaveVersion(workflowId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['workflow-save-version', workflowId],
    mutationFn: async (name?: string) => {
      if (!workflowId) throw new Error('Missing workflow ID');
      const r = await fetch(`${API_URL}/workflows/${workflowId}/save-version`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      if (!r.ok) throw await toApiError(r, 'Failed to save version');
      return r.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflow-revisions', workflowId] });
      queryClient.invalidateQueries({ queryKey: ['workflow', 'events', workflowId] });
    },
  });
}

/** Restore an immutable revision; the server appends a new revision to history. */
export function useRestoreWorkflowRevision(workflowId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['workflow-restore-revision', workflowId],
    mutationFn: async (revisionNumber: number) => {
      if (!workflowId) throw new Error('Missing workflow ID');
      const r = await fetch(
        `${API_URL}/workflows/${workflowId}/revisions/${revisionNumber}/restore`,
        {
          method: 'POST',
          credentials: 'include',
        }
      );
      if (!r.ok) throw await toApiError(r, 'Failed to restore workflow revision');
      return r.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflow-revisions', workflowId] });
      queryClient.invalidateQueries({ queryKey: ['workflow', 'events', workflowId] });
      queryClient.invalidateQueries({ queryKey: ['workflow-detail', workflowId] });
      queryClient.invalidateQueries({ queryKey: ['api-sequence', workflowId] });
    },
  });
}

/**
 * Restore a date-versioned checkpoint (`workflow_draft_snapshots` row with
 * `revisionNumber IS NULL`, written by the draft-state sync path rather than
 * an authored mutation). The server force-appends a new numbered revision
 * (provenance source 'checkpoint-restore') after restoring.
 */
export function useRestoreDraftSnapshot(workflowId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['workflow-restore-draft-snapshot', workflowId],
    mutationFn: async (draftVersion: string) => {
      if (!workflowId) throw new Error('Missing workflow ID');
      const r = await fetch(`${API_URL}/workflows/${workflowId}/restore-draft/${draftVersion}`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!r.ok) throw await toApiError(r, 'Failed to restore checkpoint');
      return r.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflow-revisions', workflowId] });
      queryClient.invalidateQueries({ queryKey: ['workflow', 'events', workflowId] });
      queryClient.invalidateQueries({ queryKey: ['workflow-detail', workflowId] });
      queryClient.invalidateQueries({ queryKey: ['api-sequence', workflowId] });
    },
  });
}
